import { useEffect, useState } from 'react';
import * as audioOps from '../../domain/audioOps';
import * as ops from '../../domain/projectOps';
import { STEPS_PER_BAR } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { songDurationSeconds } from '../../music/compile';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Alert, Button, Panel } from '../ui';

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** Song transport for the Audio workspace, plus the audio-clip bus channel strip. */
export function AudioTransport() {
  const { project, update, session } = useProject();
  const { audio, buffers } = useServices();
  const engine = useObservable(audio.state);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState(-Infinity);
  const [cacheMb, setCacheMb] = useState(0);
  const playing = engine.status === 'playing';
  const bus = project.audioBus;

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const loop = () => {
      setLevel(audio.getLevel());
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frame);
      setLevel(-Infinity);
    };
  }, [audio, playing]);

  useEffect(() => {
    const id = setInterval(() => setCacheMb(buffers.stats().bytes / 1048576), 1000);
    return () => clearInterval(id);
  }, [buffers]);

  const toggle = async () => {
    setError(null);
    try {
      if (playing) {
        await audio.stop();
        return;
      }
      // Clips live on the song timeline, so the Audio workspace always plays the song.
      if (project.transport.mode !== 'song') update((p) => ops.setTransportMode(p, 'song'));
      audio.setProject(session.snapshot.project!);
      await audio.start();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Audio could not start.');
    }
  };

  const shown = Number.isFinite(level) ? Math.max(-60, Math.min(0, level)) : -60;
  const seconds = engine.step >= 0 ? engine.step * audioOps.stepSeconds(project.settings.bpm) : 0;
  return (
    <Panel title="Song playback and audio bus">
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <Button variant={playing ? 'secondary' : 'primary'} className="min-w-28" onClick={() => void toggle()} disabled={engine.status === 'starting'} aria-pressed={playing} data-testid="audio-play">
          <span aria-hidden="true">{playing ? '■' : '▶'}</span> {playing ? 'Stop' : engine.status === 'starting' ? (engine.loadingAudio ? 'Loading audio…' : 'Starting…') : 'Play song'}
        </Button>
        <div className="flex min-w-40 flex-col gap-1">
          <label htmlFor="bus-volume" className="text-xs font-medium text-zinc-300">
            Clip bus volume {bus.volumeDb} dB
          </label>
          <input id="bus-volume" type="range" min={-60} max={6} step={0.5} value={bus.volumeDb} onChange={(e) => update((p) => audioOps.updateAudioBus(p, { volumeDb: Number(e.target.value) }))} />
        </div>
        <div className="flex min-w-28 flex-col gap-1">
          <label htmlFor="bus-pan" className="text-xs font-medium text-zinc-300">
            Pan {bus.pan === 0 ? 'C' : bus.pan < 0 ? `L${Math.round(-bus.pan * 100)}` : `R${Math.round(bus.pan * 100)}`}
          </label>
          <input id="bus-pan" type="range" min={-1} max={1} step={0.05} value={bus.pan} onChange={(e) => update((p) => audioOps.updateAudioBus(p, { pan: Number(e.target.value) }))} />
        </div>
        <div className="flex min-w-28 flex-col gap-1">
          <label htmlFor="bus-reverb" className="text-xs font-medium text-zinc-300">
            Reverb {Math.round(bus.reverbSend * 100)}%
          </label>
          <input id="bus-reverb" type="range" min={0} max={1} step={0.01} value={bus.reverbSend} onChange={(e) => update((p) => audioOps.updateAudioBus(p, { reverbSend: Number(e.target.value) }))} />
        </div>
        <div className="flex min-w-28 flex-col gap-1">
          <label htmlFor="bus-delay" className="text-xs font-medium text-zinc-300">
            Delay {Math.round(bus.delaySend * 100)}%
          </label>
          <input id="bus-delay" type="range" min={0} max={1} step={0.01} value={bus.delaySend} onChange={(e) => update((p) => audioOps.updateAudioBus(p, { delaySend: Number(e.target.value) }))} />
        </div>
        <div className="flex gap-1 pb-0.5">
          <Button size="sm" aria-pressed={bus.muted} variant={bus.muted ? 'danger' : 'secondary'} onClick={() => update((p) => audioOps.updateAudioBus(p, { muted: !p.audioBus.muted }))}>
            Mute clips
          </Button>
          <Button size="sm" aria-pressed={bus.solo} variant={bus.solo ? 'primary' : 'secondary'} onClick={() => update((p) => audioOps.updateAudioBus(p, { solo: !p.audioBus.solo }))}>
            Solo clips
          </Button>
        </div>
        <div className="flex min-w-40 flex-col gap-1">
          <label htmlFor="audio-level" className="text-xs font-medium text-zinc-300">
            Output {playing ? `${shown.toFixed(0)} dBFS` : '(not playing)'}
          </label>
          <meter id="audio-level" min={-60} max={0} low={-18} high={-6} optimum={-24} value={shown} className="h-4 w-full" />
        </div>
      </div>
      <p className="mt-3 text-xs text-zinc-400" role="status" data-testid="audio-ws-status" data-status={engine.status} data-loading={engine.loadingAudio ? 'true' : 'false'}>
        {engine.contextState === 'none' ? 'Audio is off until you press Play.' : `Audio ${engine.status}`}
        {playing && engine.step >= 0 ? ` · bar ${Math.floor(engine.step / STEPS_PER_BAR) + 1} · ${fmt(seconds)} / ${fmt(songDurationSeconds(project))}` : ''}
        {` · decoded audio in memory: ${cacheMb.toFixed(1)} MB of ${(buffers.budgetBytes / 1048576).toFixed(0)} MB`}
      </p>
      {(error || engine.assetError) && (
        <div className="mt-3" aria-live="assertive">
          <Alert title={error ? 'Playback could not start' : 'An audio file could not be played'}>{error ?? engine.assetError}</Alert>
        </div>
      )}
    </Panel>
  );
}
